import {
  AcButton,
  AcCheckbox,
  AcFormCard,
  AcFormInput,
} from '@components'
import { GLOBAL_ERRORS, LABELS } from '@constants'
import { AcInputBase } from 'src/components/core/ac-input-base/ac-input-base'
import { ISandwichSubscriptionPayload } from '@typings'
import { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styles from './sandwich-subscription.module.scss'

const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
] as const

const WEEKDAY_LABELS: Record<string, string> = {
  monday: 'Maandag',
  tuesday: 'Dinsdag',
  wednesday: 'Woensdag',
  thursday: 'Donderdag',
  friday: 'Vrijdag',
}

const SANDWICH_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  label: String((i + 1) * 10) + ' broodjes',
  value: (i + 1) * 10,
}))

type ISandwichSubscriptionForm = {
  onSubmit: () => void
  loading: boolean
  daysError: string | null
  currentStep: number
  totalSteps: number
  onNext: () => void
  onBack: () => void
  validating: boolean
}

export const SandwichSubscriptionForm = ({
  onSubmit,
  loading,
  daysError,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  validating,
}: ISandwichSubscriptionForm) => {
  const FormInput = AcFormInput<ISandwichSubscriptionPayload>
  const form = useFormContext<ISandwichSubscriptionPayload>()

  const minDate = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow.toISOString().split('T')[0]
  }, [])

  const renderStep1 = () => (
    <AcFormCard title={LABELS.DELIVERY_DAYS} className={styles['form-card-green']}>
      <div className={styles['delivery-day-row']}>
        <Controller
          name="start_date"
          control={form.control}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <div style={{ width: '100%' }}>
              <label htmlFor="start_date">{LABELS.START_DATE}*</label>
              <AcInputBase error={fieldState.error?.message}>
                <input
                  className={styles['date-input']}
                  type="date"
                  id="start_date"
                  min={minDate}
                  {...field}
                />
              </AcInputBase>
            </div>
          )}
        />
      </div>

      {WEEKDAY_KEYS.map(day => (
        <DeliveryDayRow key={day} day={day} />
      ))}

      {daysError && (
        <div className={styles['delivery-days-error']}>{daysError}</div>
      )}
    </AcFormCard>
  )

  const renderStep2 = () => (
    <AcFormCard title={LABELS.COMPANY_INFORMATION} className={styles['form-card-green']}>
      <FormInput
        name="company_name"
        label={LABELS.COMPANY_NAME}
        validate="required"
      />
      <FormInput
        name="invoice_company"
        label={LABELS.INVOICE_COMPANY}
      />
      <FormInput
        name="invoice_email"
        label={LABELS.INVOICE_EMAIL}
        type="email"
        validate="required|email"
      />
    </AcFormCard>
  )

  const renderStep3 = () => (
    <AcFormCard title={LABELS.CONTACT_INFORMATION} className={styles['form-card-green']}>
      <FormInput
        name="first_name"
        label={LABELS.NAME_FIRST}
        validate="required"
      />
      <FormInput
        name="last_name"
        label={LABELS.NAME_LAST}
        validate="required"
      />
      <FormInput
        name="email"
        label={LABELS.EMAIL}
        type="email"
        validate="required|email"
      />
      <FormInput
        name="phone"
        label={LABELS.PHONE}
        type="tel"
        validate="required"
      />
    </AcFormCard>
  )

  const renderStep4 = () => (
    <AcFormCard title={LABELS.ADDRESS_INFORMATION} className={styles['form-card-green']}>
      <FormInput
        name="street"
        label={LABELS.STREET}
        validate="required"
      />
      <FormInput
        name="house_number"
        label={LABELS.HOUSE_NUMBER}
        type="number"
        validate="required"
      />
      <FormInput
        name="house_number_addition"
        label={LABELS.HOUSE_NUMBER_ADDITION}
      />
      <FormInput
        name="postal_code"
        label={LABELS.POSTALCODE}
        validate="required"
      />
      <FormInput
        name="city"
        label={LABELS.CITY}
        validate="required"
      />
    </AcFormCard>
  )

  const renderStep5 = () => (
    <>
      <SummarySection />

      <div className={styles['terms-row']}>
        <Controller
          name="tac"
          control={form.control}
          rules={{ validate: v => v || LABELS.TAC_REQUIRED }}
          render={({ field, fieldState }) => (
            <div>
              <div className={styles['terms-checkbox']}>
                <AcCheckbox
                  label="Algemene voorwaarden"
                  checked={field.value}
                  onClick={() => field.onChange(!field.value)}
                />
                <span className={styles['terms-label']}>
                  Ik heb de{' '}
                  <a
                    href="https://debuurtboer.nl/algemenevoorwaarden/"
                    target="_blank"
                    rel="noopener noreferrer">
                    algemene voorwaarden
                  </a>{' '}
                  gelezen en ga akkoord
                </span>
              </div>
              {fieldState.error?.message && (
                <div className={styles['terms-error']}>
                  {fieldState.error.message}
                </div>
              )}
            </div>
          )}
        />
      </div>
    </>
  )

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1()
      case 2:
        return renderStep2()
      case 3:
        return renderStep3()
      case 4:
        return renderStep4()
      case 5:
        return renderStep5()
      default:
        return null
    }
  }

  return (
    <div className={styles['sandwich-subscription-form']}>
      <div key={currentStep} className={styles['step-content']}>
        {renderCurrentStep()}
      </div>

      <div className={styles['navigation-row']}>
        {currentStep > 1 && (
          <AcButton
            label={LABELS.PREVIOUS}
            variant="text"
            onClick={onBack}
            disabled={loading || validating}
          />
        )}
        {Object.keys(form.formState.errors).length > 0 && (
          <span className={styles['navigation-row__error']}>
            {GLOBAL_ERRORS.FORM_STATE_INVALID}
          </span>
        )}
        <div className={styles['navigation-row__spacer']} />
        {currentStep < totalSteps ? (
          <AcButton
            label={LABELS.NEXT}
            onClick={onNext}
            disabled={loading || validating}
          />
        ) : (
          <AcButton
            label={LABELS.SUBMIT}
            onClick={onSubmit}
            disabled={loading || validating}
          />
        )}
      </div>
    </div>
  )
}

const SummaryField = ({ label, value }: { label: string; value?: string | null }) => (
  <div className={styles['summary__field']}>
    <span className={styles['summary__label']}>{label}</span>
    <span className={styles['summary__value']}>{value || '-'}</span>
  </div>
)

const SummarySection = () => {
  const form = useFormContext<ISandwichSubscriptionPayload>()
  const values = useWatch({ control: form.control })

  const activeDays = WEEKDAY_KEYS.filter(
    day => values[day] !== null && values[day] !== undefined
  )

  const address = [
    [values.street, values.house_number].filter(Boolean).join(' '),
    values.house_number_addition,
  ]
    .filter(Boolean)
    .join(' ')

  const postalCity = [values.postal_code, values.city]
    .filter(Boolean)
    .join(' ')

  const totalSandwiches = activeDays.reduce(
    (sum, day) => sum + (Number(values[day]) || 0),
    0
  )

  return (
    <div className={styles['summary']}>
      <AcFormCard title={LABELS.OVERVIEW} className={styles['form-card-green']}>
        <div className={styles['summary__days']}>
          <div className={styles['summary__days-header']}>
            <span className={styles['summary__days-start']}>
              {LABELS.START_DATE}: <strong>{values.start_date || '-'}</strong>
            </span>
            <span className={styles['summary__days-total']}>
              Totaal: {totalSandwiches} broodjes / week
            </span>
          </div>
          <div className={styles['summary__days-grid']}>
            {WEEKDAY_KEYS.map(day => {
              const isActive =
                values[day] !== null && values[day] !== undefined
              return (
                <div
                  key={day}
                  className={
                    styles[
                      isActive
                        ? 'summary__day-card--active'
                        : 'summary__day-card'
                      ]
                  }>
                  <span className={styles['summary__day-name']}>
                    {WEEKDAY_LABELS[day]}
                  </span>
                  <span className={styles['summary__day-count']}>
                    {isActive ? values[day] : '-'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <SummaryField label={LABELS.COMPANY_NAME} value={values.company_name} />
        {values.invoice_company && (
          <SummaryField label={LABELS.INVOICE_COMPANY} value={values.invoice_company} />
        )}
        <SummaryField label={LABELS.INVOICE_EMAIL} value={values.invoice_email} />
        <SummaryField
          label={LABELS.CONTACT_INFORMATION}
          value={`${values.first_name || ''} ${values.last_name || ''}`.trim()}
        />
        <SummaryField label={LABELS.EMAIL} value={values.email} />
        <SummaryField label={LABELS.PHONE} value={values.phone} />
        <SummaryField label={LABELS.ADDRESS_INFORMATION} value={address} />
        <SummaryField
          label={LABELS.POSTALCODE + ' / ' + LABELS.CITY}
          value={postalCity}
        />
      </AcFormCard>
    </div>
  )
}

const DeliveryDayRow = ({
  day,
}: {
  day: (typeof WEEKDAY_KEYS)[number]
}) => {
  const form = useFormContext<ISandwichSubscriptionPayload>()
  const value = useWatch({ control: form.control, name: day })
  const isChecked = value !== null && value !== undefined

  const handleToggle = () => {
    if (isChecked) {
      form.setValue(day, null, { shouldDirty: true })
    } else {
      form.setValue(day, 10, { shouldDirty: true })
    }
  }

  return (
    <div className={styles['delivery-day-row']}>
      <div className={styles['delivery-day-checkbox']}>
        <AcCheckbox
          label={WEEKDAY_LABELS[day]}
          withLabel
          checked={isChecked}
          onClick={handleToggle}
        />
      </div>
      {isChecked && (
        <div className={styles['delivery-day-select']}>
          <AcFormInput<ISandwichSubscriptionPayload>
            name={day}
            label={LABELS.NUMBER_OF_SANDWICHES}
            type="select"
            options={SANDWICH_OPTIONS}
            labelKey="label"
            valueKey="value"
            inputFormat="value"
            outputFormat="value"
            withLabel={false}
          />
        </div>
      )}
    </div>
  )
}
